import {
  useFindAllCompaniesQuery,
  useFindOneOrderQuery,
  useRemoveOrderMutation,
  useUploadPurchaseOrderMutation,
  useUploadQuoteMutation,
} from '../../api/api-hooks';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { truncateISOString } from '../../utils/dateTime';
import { OrderForm } from './ordersTypes';
import useOrderItemCreator from './useOrderItemCreator';
import useOrderItemUpdater from './useOrderItemUpdater';
import { ordersMiddlware, orderValidator } from './ordersUtils';
import UploadButton from '../../components/UploadButton';

interface Props {
  selectedOrders: Item[];
  setSelectedOrders: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}
const addFormInitialState: OrderForm = {
  company: null,
  purchaseOrderLink: undefined,
  purchaseOrderName: undefined,
  quoteLink: undefined,
  quoteName: undefined,
  price: undefined,
  // Default order date to today
  orderDate: truncateISOString(new Date().toISOString()),
  completed: false,
  notes: '',
};

const OrdersOptionsBar = ({
  selectedOrders,
  setSelectedOrders,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteOrder] = useRemoveOrderMutation();
  const itemCreator = useOrderItemCreator();
  const itemUpdater = useOrderItemUpdater();
  const { data: companies } = useFindAllCompaniesQuery({});
  if (companies === undefined) return null;
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedOrders}
        setSelectedItems={setSelectedOrders}
        handlerMiddleware={ordersMiddlware}
        itemValidator={orderValidator}
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        formInstructions='Company, filled date, and price are required.'
        addFormInitialState={addFormInitialState}
        itemName='Order'
        deleteNotes='Deleting this order will not delete any associated hardware, software, or RMAs.'
        childrenCreator={(addFormState: OrderForm, handler: any) => (
          <Grid container spacing={3}>
            <Grid container item xs direction='column' spacing={1}>
              <Grid style={{ marginBottom: '.3rem' }} item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'company');
                  }}
                  value={addFormState.company}
                  options={companies}
                  getOptionLabel={(company) => company.companyName}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Company'
                      name='company'
                      variant='outlined'
                      id='company'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='purchaseOrderLink'
                  label='Purchase Order Link'
                  fullWidth
                  value={nullStringCheck(addFormState.purchaseOrderLink)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <UploadButton
                  uploadHook={useUploadPurchaseOrderMutation}
                  handler={handler}
                  fieldToSet='purchaseOrderLink'
                  itemName='Purchase Order'
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='purchaseOrderName'
                  label='Purchase Order Name'
                  fullWidth
                  value={nullStringCheck(addFormState.purchaseOrderName)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='notes'
                  label='Notes'
                  multiline
                  fullWidth
                  value={nullStringCheck(addFormState.notes)}
                  onChange={handler}
                  rows={5}
                />
              </Grid>
            </Grid>
            <Grid container item xs direction='column' spacing={1}>
              <Grid item>
                <TextField
                  margin='dense'
                  name='quoteLink'
                  label='Quote Link'
                  fullWidth
                  value={nullStringCheck(addFormState.quoteLink)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <UploadButton
                  uploadHook={useUploadQuoteMutation}
                  handler={handler}
                  fieldToSet='quoteLink'
                  itemName='Quote'
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='quoteName'
                  label='Quote Name'
                  fullWidth
                  value={nullStringCheck(addFormState.quoteName)}
                  onChange={handler}
                />
              </Grid>

              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Filled Date: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='orderDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.orderDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='price'
                  label='Price'
                  fullWidth
                  type='number'
                  value={nullStringCheck(addFormState.price)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addFormState.completed}
                      onChange={() =>
                        handler({
                          target: {
                            name: 'completed',
                            value: !addFormState.completed,
                          },
                        })
                      }
                    />
                  }
                  label='Completed'
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={itemCreator}
        itemGetter={useFindOneOrderQuery}
        itemUpdater={itemUpdater}
        itemDeleter={deleteOrder}
      />
    </>
  );
};

export default OrdersOptionsBar;
